/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, {Component} from 'react';
import {withRouter} from "react-router-dom";

import {isUserSignedIn, UserContext} from "../UserContext";
import WaitingComponent from "../components/WaitingComponent";
import AccountNavbar from "./AccountNavbar";
import AppFooter from "../components/AppFooter";
import AccountSignedOutEmptyState from "./AccountSignedOutEmptyState";
import {get, handleErrorFetch, put} from "../services/APIService";
import {ToastContainer} from "react-toastify";
import {onKeyDown} from "../helpers/JsHelper";
import {alertMailType, alertMessageType} from "../helpers/AccountHelper";
import {failedToast, successToast} from "../helpers/ToastHelper";

class WorkspaceNotifications extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);

        this.state = {
            workspaceAccess: null
        };

        this.emailInput = React.createRef();
        this.alertMailSelect = React.createRef();
        this.alertMessageSelect = React.createRef();
    }

    componentDidMount() {
        document.title = "Fing - Edit workspace notifications";
        const query = new URLSearchParams(this.props.location.search);
        const accessId = query && query.has("aid") ? query.get("aid") : null;
        this.fetchWorkspaceAccessList(accessId);
    }

    fetchWorkspaceAccessList(accessId) {
        get(`/rest/workspace/access/${accessId}`)
            .then((access) => {
                if (!access) {
                    this.setState({workspaceAccess: {}})
                    return;
                }

                this.setState({workspaceAccess: access});
            })
            .catch((e) => {
                console.log('Error in get auth apps: ', e);
                this.setState({workspaceAccess: {}});
            });
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(response) {
    }


    render() {
        return (
            <div>
                <AccountNavbar mode='account' />

                <main id="content" role="main">
                    {this.renderMainContent()}
                </main>

                <AppFooter />
            </div>
        );
    }

    renderMainContent() {
        return (
            <UserContext.Consumer>
                {userContext => this.renderMainContentInContext(userContext)}
            </UserContext.Consumer>
        )
    }

    renderMainContentInContext(userContext) {
        if (!userContext || !userContext.userProfileLoaded) {
            return <div className="container space-2">
                <div className="row mt-3">
                    <div className="col-lg-12 text-center">
                        <WaitingComponent className="my-3" />
                    </div>
                </div>
            </div>;
        } else if (isUserSignedIn(userContext) && userContext.stripePublicApiKey) {
            return this.renderMainContentSignedIn(userContext);
        } else {
            return this.renderMainContentPromo();
        }
    }

    renderMainContentSignedIn(userContext) {
        const {workspaceAccess} = this.state;

        if (!workspaceAccess) { // Still loading
            return this.renderLoading();
        }

        if (workspaceAccess && !workspaceAccess.workspaceId) { // Loaded but not found
            return this.renderNotFound();
        }

        const validateEmail = function (email) {
            const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(email).toLowerCase());
        };

        const handleSubmit = (event) => {
            event.preventDefault();

            let alertMail = this.emailInput.current.value ? this.emailInput.current.value.trim() : '';
            if (alertMail.length === 0) {
                alertMail = null;
            } else if (!validateEmail(alertMail)) {
                failedToast("Email '" + alertMail + "' is not a valid email");
                return;
            }

            const payload = {
                accessId: workspaceAccess.accessId,
                mail: alertMail,
                mailType: this.alertMailSelect.current.value,
                messageType: this.alertMessageSelect.current.value,
            };

            put('/rest/workspace/access/customize', payload)
                .then((newAccess) => {
                    successToast("Notification settings updated.");
                    this.setState({workspaceAccess: newAccess});
                })
                .catch((error) => {
                    failedToast("Something went wrong.");

                    if (error && error.code === 401) {
                        let newContext = handleErrorFetch(userContext);
                        userContext.userContextUpdater(newContext);
                    }

                    console.log('Error in edit access settings: ', error)
                })
        };

        return (
            <div className="bg-light">
                <div className="container space-1">

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h3 className="mb-0">Notifications of {workspaceAccess.workspaceName}</h3>
                            <p className="text-muted mb-0">Review and manage your notifications</p>
                        </div>
                    </div>

                    <form className="form" onSubmit={handleSubmit}>
                        <div className="row my-4">
                            <div className="col-sm-12 col-md-3 py-2">
                                <h4>Network events</h4>
                            </div>

                            <div className="col-sm-12 col-md-9">
                                <div className="card card-bordered">
                                    <div className="card-body">

                                        {/* ============ User ============ */}
                                        <div className="row align-items-center">
                                            <div className="col-lg-4">
                                                <label>User ID</label>
                                            </div>
                                            <div className="col-lg-8">
                                                <div className="form-group pt-2">
                                                    <input className="form-control" disabled={true}
                                                           name="userId"
                                                           defaultValue={workspaceAccess.userId}/>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ============ Email ============ */}
                                        <div className="row align-items-center">
                                            <div className="col-lg-4">
                                                <label>Email address</label>
                                            </div>
                                            <div className="col-lg-8">
                                                <div className="form-group pt-2">
                                                    <input className="form-control" onKeyDown={onKeyDown}
                                                           name="name" placeholder="e.g. john.smith@fing.com"
                                                           ref={this.emailInput}
                                                           defaultValue={workspaceAccess.alertEmail}/>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ============ Alert Type ============ */}
                                        <div className="row align-items-center">
                                            <div className="col-lg-4">
                                                <label>Email network events</label>
                                            </div>
                                            <div className="col-lg-8">
                                                <div className="form-group pt-2">
                                                    <select className="form-control custom-select" id="alertMailSelect"
                                                            ref={this.alertMailSelect}
                                                            defaultValue={workspaceAccess.alertMailType}>
                                                        {alertMailType.map((type, idx) =>
                                                            <option key={"opt_alertMail_" + idx}
                                                                    value={type.value}>{type.text}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ============ Message Type ============ */}
                                        <div className="row align-items-center">
                                            <div className="col-lg-4">
                                                <label>Mobile notification events</label>
                                            </div>
                                            <div className="col-lg-8">
                                                <div className="form-group pt-2">
                                                    <select className="form-control custom-select" id="alertMessageSelect"
                                                            ref={this.alertMessageSelect}
                                                            defaultValue={workspaceAccess.alertMessageType}>
                                                        {alertMessageType.map((type, idx) =>
                                                            <option key={"opt_alertMessage_" + idx}
                                                                    value={type.value}>{type.text}</option>)}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row my-4">
                            <div className="col-sm-12 col-md-9 offset-md-3">
                                <button className="btn btn-primary btn-sm" type="submit">Save Changes</button>
                            </div>
                        </div>
                    </form>

                </div>
                <ToastContainer />
            </div>
        );
    }

    renderNotFound() {
        return <div className="bg-light">
            <div className="container space-1">
                <div className="row justify-content-md-between align-items-md-center mb-10">
                    <div className="col-col-md-7 col-xl-6">
                        <div className="mb-6">
                            <h1 className="display-5 mb-3">Workspace access not found</h1>
                            <p className="lead mb-9">Please check your access level.</p>
                        </div>
                        <div>
                            <a className="btn btn-primary btn-wide mr-lg-4"
                               href="/account/workspace">Back to the list of workspaces</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>;
    }

    renderLoading() {
        return <div className="container space-2">
            <div className="row mt-3">
                <div className="col-lg-12 text-center">
                    <WaitingComponent className="my-3"/>
                </div>
            </div>
        </div>;
    }

    renderMainContentPromo() {
        return <AccountSignedOutEmptyState />;
    }

}

export default withRouter(WorkspaceNotifications)